import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Braces,
  Copy,
  Play,
  Plus,
  Save,
  Search,
  Trash2,
} from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { ModelPicker } from "../../components/ui/ModelPicker";
import {
  cloneRecognizerForCustomer,
  createRecognizer,
  deleteRecognizer,
  getRecognizer,
  listRecognizers,
  testRecognizer,
  updateRecognizer,
  type RecognizerDetail,
  type RecognizerIntentRow,
  type RecognizerListItem,
  type RecognizerSlotRow,
  type RecognizerTestResult,
  type RecognizerUpsertPayload,
} from "../../api/recognizerAdminApi";
import "../../styles/recognizers.css";

type ListState =
  | { status: "loading" }
  | { status: "ready"; items: RecognizerListItem[] }
  | { status: "error"; message: string };

/**
 * Admin page for `dbo.Recognizer`. Master list on the left (global rows
 * and customer overrides), a tabbed editor on the right: overview, prompt
 * and schema, intents and slots, and a test panel that runs the unsaved
 * form state against the LLM.
 */
export function RecognizersAdminPage() {
  const [list, setList] = useState<ListState>({ status: "loading" });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setList({ status: "loading" });
    try {
      const resp = await listRecognizers();
      setList({ status: "ready", items: resp.items ?? [] });
    } catch (ex) {
      setList({ status: "error", message: ex instanceof Error ? ex.message : String(ex) });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (list.status !== "ready" || creating || selectedId != null) return;
    const first = list.items[0];
    if (first) setSelectedId(first.recognizerId);
  }, [list, creating, selectedId]);

  return (
    <>
      <PageHeader
        title="Recognizers"
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setSelectedId(null);
              setCreating(true);
            }}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={16} />
            New recognizer
          </button>
        }
      />
      <div className="container" style={{ padding: "0 0 32px" }}>
        {list.status === "loading" && <div className="tile">Loading…</div>}
        {list.status === "error" && (
          <div className="tile" style={{ borderColor: "var(--portal-danger)" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <AlertTriangle size={18} />
              <span>Could not load recognizers: {list.message}</span>
            </div>
          </div>
        )}
        {list.status === "ready" && (
          <div className="rz">
            <RecognizerList
              items={list.items}
              selectedId={creating ? null : selectedId}
              onSelect={(id) => {
                setCreating(false);
                setSelectedId(id);
              }}
              onCloned={async (newId) => {
                await reload();
                setCreating(false);
                setSelectedId(newId);
              }}
            />
            <div className="rz-detail">
              {creating ? (
                <RecognizerForm
                  key="new"
                  mode="create"
                  onSaved={async (id) => {
                    await reload();
                    setCreating(false);
                    setSelectedId(id);
                  }}
                  onCancel={() => setCreating(false)}
                />
              ) : selectedId != null ? (
                <RecognizerForm
                  key={selectedId}
                  mode="edit"
                  recognizerId={selectedId}
                  onSaved={async () => {
                    await reload();
                  }}
                  onDeleted={async () => {
                    await reload();
                    setSelectedId(null);
                  }}
                />
              ) : (
                <div className="rz-empty">
                  Pick a recognizer on the left, or create a new one.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Master list                                                         */
/* ------------------------------------------------------------------ */

function RecognizerList({
  items,
  selectedId,
  onSelect,
  onCloned,
}: {
  items: RecognizerListItem[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onCloned: (newId: number) => void | Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      items.filter(
        (r) =>
          !q ||
          r.recognizerKey.toLowerCase().includes(q) ||
          (r.displayLabel ?? "").toLowerCase().includes(q),
      ),
    [items, q],
  );
  const globals = filtered.filter((r) => r.customerId == null);
  const overrides = filtered.filter((r) => r.customerId != null);

  return (
    <aside className="rz-master">
      <label className="rz-search">
        <Search size={15} />
        <input
          type="search"
          value={query}
          placeholder="Search by key or label"
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {items.length === 0 && <div className="rz-empty">No recognizers yet.</div>}
      {items.length > 0 && filtered.length === 0 && (
        <div className="rz-empty">Nothing matches “{query}”.</div>
      )}
      {globals.length > 0 && (
        <>
          <div className="rz-group">Global</div>
          {globals.map((r) => (
            <ListRow key={r.recognizerId} item={r} active={r.recognizerId === selectedId} onSelect={onSelect} onCloned={onCloned} />
          ))}
        </>
      )}
      {overrides.length > 0 && (
        <>
          <div className="rz-group">Customer overrides</div>
          {overrides.map((r) => (
            <ListRow key={r.recognizerId} item={r} active={r.recognizerId === selectedId} onSelect={onSelect} onCloned={onCloned} />
          ))}
        </>
      )}
    </aside>
  );
}

function ListRow({
  item,
  active,
  onSelect,
  onCloned,
}: {
  item: RecognizerListItem;
  active: boolean;
  onSelect: (id: number) => void;
  onCloned: (newId: number) => void | Promise<void>;
}) {
  const isGlobal = item.customerId == null;
  return (
    <div className="rz-row">
      <button
        type="button"
        className={`rz-item${active ? " rz-item--active" : ""}${item.isActive ? "" : " rz-item--inactive"}`}
        onClick={() => onSelect(item.recognizerId)}
        title={item.isActive ? undefined : "Inactive"}
      >
        <span className="rz-item-dot" />
        <span className="rz-item-text">
          <span className="rz-item-name">{item.displayLabel || item.recognizerKey}</span>
          <span className="rz-item-meta">
            <code>{item.recognizerKey}</code>
            {!isGlobal && <> · customer {item.customerId}</>}
            {" · "}
            {item.intentCount} {item.intentCount === 1 ? "intent" : "intents"}
          </span>
        </span>
      </button>
      {isGlobal && (
        <div className="rz-item-actions">
          <CloneAction recognizerId={item.recognizerId} onCloned={onCloned} />
        </div>
      )}
    </div>
  );
}

function CloneAction({
  recognizerId,
  onCloned,
}: {
  recognizerId: number;
  onCloned: (newId: number) => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    const raw = window.prompt(
      "Customer override: enter the target CustomerId. The recognizer will be cloned for that customer.",
    );
    if (raw == null) return;
    const customerId = Number(raw.trim());
    if (!Number.isFinite(customerId) || customerId <= 0) {
      window.alert("Invalid CustomerId.");
      return;
    }
    setBusy(true);
    try {
      const res = await cloneRecognizerForCustomer(recognizerId, customerId);
      await onCloned(res.id);
    } catch (ex) {
      window.alert(`Clone failed: ${ex instanceof Error ? ex.message : String(ex)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      className="rz-icon-btn"
      onClick={handle}
      disabled={busy}
      title="Clone as customer override"
      aria-label="Clone as customer override"
    >
      <Copy size={14} />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Detail / editor                                                     */
/* ------------------------------------------------------------------ */

const EMPTY: RecognizerDetail = {
  recognizerId: 0,
  recognizerKey: "",
  displayLabel: "",
  description: "",
  customerId: null,
  llmModel: "",
  systemPrompt: "",
  outputSchema: "",
  isActive: true,
  sortOrder: null,
  intents: [],
  slots: [],
};

type Tab = "overview" | "prompt" | "intents" | "test";

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "prompt", label: "Prompt & schema" },
  { id: "intents", label: "Intents & slots" },
  { id: "test", label: "Test" },
];

interface FormProps {
  mode: "create" | "edit";
  recognizerId?: number;
  onSaved: (id: number) => void | Promise<void>;
  onDeleted?: () => void | Promise<void>;
  onCancel?: () => void;
}

function toPayload(detail: RecognizerDetail): RecognizerUpsertPayload {
  return {
    recognizerKey: detail.recognizerKey.trim(),
    displayLabel: detail.displayLabel || null,
    description: detail.description || null,
    customerId: detail.customerId ?? null,
    llmModel: detail.llmModel?.trim() || null,
    systemPrompt: detail.systemPrompt || null,
    outputSchema: detail.outputSchema || null,
    isActive: detail.isActive,
    sortOrder: detail.sortOrder,
    intents: detail.intents.map((i, idx) => ({
      recognizerIntentId: i.recognizerIntentId,
      intentKey: i.intentKey.trim(),
      displayLabel: i.displayLabel || null,
      description: i.description || null,
      sortOrder: i.sortOrder ?? idx,
    })),
    slots: detail.slots.map((s, idx) => ({
      recognizerSlotId: s.recognizerSlotId,
      slotKey: s.slotKey.trim(),
      displayLabel: s.displayLabel || null,
      dataType: s.dataType || null,
      isRequired: s.isRequired,
      sortOrder: s.sortOrder ?? idx,
    })),
  };
}

function RecognizerForm({ mode, recognizerId, onSaved, onDeleted, onCancel }: FormProps) {
  const [detail, setDetail] = useState<RecognizerDetail | null>(
    mode === "create" ? { ...EMPTY } : null,
  );
  const [baseline, setBaseline] = useState<string>(
    mode === "create" ? JSON.stringify(toPayload(EMPTY)) : "",
  );
  const [tab, setTab] = useState<Tab>("overview");
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (mode === "create" || recognizerId == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getRecognizer(recognizerId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setBaseline(JSON.stringify(toPayload(d)));
      })
      .catch((ex: Error) => {
        if (!cancelled) setError(ex.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, recognizerId]);

  const dirty = detail != null && JSON.stringify(toPayload(detail)) !== baseline;

  const patch = (p: Partial<RecognizerDetail>) => {
    setDetail((d) => (d ? { ...d, ...p } : d));
    setSavedAt(null);
  };

  const save = useCallback(async () => {
    if (!detail || saving) return;
    setError(null);
    setSaving(true);
    try {
      const payload = toPayload(detail);
      if (mode === "create") {
        const res = await createRecognizer(payload);
        await onSaved(res.id);
      } else if (recognizerId != null) {
        await updateRecognizer(recognizerId, payload);
        setBaseline(JSON.stringify(payload));
        setSavedAt(Date.now());
        await onSaved(recognizerId);
      }
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  }, [detail, saving, mode, recognizerId, onSaved]);

  // Ctrl/Cmd+S saves from anywhere in the editor.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  const onDelete = async () => {
    if (recognizerId == null || !onDeleted) return;
    if (!window.confirm("Delete this recognizer? This cannot be undone.")) return;
    setSaving(true);
    try {
      await deleteRecognizer(recognizerId);
      await onDeleted();
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="rz-empty">Loading…</div>;
  if (!detail) return <div className="rz-empty">{error ?? "Nothing to show."}</div>;

  const isGlobal = detail.customerId == null;
  const title =
    mode === "create"
      ? "New recognizer"
      : detail.displayLabel?.trim() || detail.recognizerKey || `Recognizer #${detail.recognizerId}`;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <header className="rz-head">
        <div className="rz-head-text">
          <h2 className="rz-title">{title}</h2>
          <div className="rz-subtitle">
            {detail.recognizerKey && (
              <span className="rz-chip">
                <code>{detail.recognizerKey}</code>
              </span>
            )}
            {mode === "edit" && (
              <span className={`rz-chip ${isGlobal ? "rz-chip--global" : "rz-chip--customer"}`}>
                {isGlobal ? "Global" : `Customer ${detail.customerId}`}
              </span>
            )}
            {!detail.isActive && <span className="rz-chip rz-chip--inactive">Inactive</span>}
            {dirty && <span className="rz-dirty">Unsaved changes</span>}
            {!dirty && savedAt != null && <span>Saved</span>}
          </div>
        </div>
        <div className="rz-head-actions">
          {mode === "edit" && onDeleted && (
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={onDelete}
              disabled={saving}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Trash2 size={14} />
              Delete
            </button>
          )}
          {onCancel && (
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || (mode === "edit" && !dirty)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            title="Save (Ctrl/Cmd+S)"
          >
            <Save size={14} />
            {saving ? "Saving…" : mode === "create" ? "Create" : "Save"}
          </button>
        </div>
      </header>

      <nav className="rz-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`rz-tab${tab === t.id ? " rz-tab--active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === "intents" && (
              <span className="rz-tab-count">
                {detail.intents.length}/{detail.slots.length}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="rz-body">
        {tab === "overview" && <OverviewTab detail={detail} mode={mode} patch={patch} />}
        {tab === "prompt" && <PromptTab detail={detail} patch={patch} />}
        {tab === "intents" && (
          <>
            <IntentEditor intents={detail.intents} onChange={(intents) => patch({ intents })} />
            <SlotEditor slots={detail.slots} onChange={(slots) => patch({ slots })} />
          </>
        )}
        {tab === "test" && <RecognizerTester detail={detail} />}

        {error && <div className="rz-notice rz-notice--error">{error}</div>}
      </div>
    </form>
  );
}

/* ---------- Overview ---------- */

function OverviewTab({
  detail,
  mode,
  patch,
}: {
  detail: RecognizerDetail;
  mode: "create" | "edit";
  patch: (p: Partial<RecognizerDetail>) => void;
}) {
  return (
    <div className="rz-grid">
      <Field label="Recognizer key" required hint="Unique within its scope. Flows reference this key from the Ask node.">
        <input
          type="text"
          className="rz-input"
          value={detail.recognizerKey}
          onChange={(e) => patch({ recognizerKey: e.target.value })}
          required
          autoFocus={mode === "create"}
        />
      </Field>
      <Field label="Display label" hint="Shown in the flow builder instead of the key.">
        <input
          type="text"
          className="rz-input"
          value={detail.displayLabel ?? ""}
          onChange={(e) => patch({ displayLabel: e.target.value })}
        />
      </Field>
      <Field label="Description" wide>
        <input
          type="text"
          className="rz-input"
          value={detail.description ?? ""}
          onChange={(e) => patch({ description: e.target.value })}
        />
      </Field>
      <Field
        label="Model"
        wide
        hint="Which Claude model runs this recognizer. Smaller models answer faster; the model is the biggest factor in the pause after the caller stops talking."
      >
        <ModelPicker
          value={detail.llmModel ?? ""}
          onChange={(v) => patch({ llmModel: v })}
        />
      </Field>
      <Field label="Customer" hint="Leave empty for a global recognizer. Set a CustomerId to make this an override for one customer.">
        <input
          type="number"
          className="rz-input"
          value={detail.customerId ?? ""}
          placeholder="Global"
          onChange={(e) => {
            const v = e.target.value.trim();
            patch({ customerId: v === "" ? null : Number(v) });
          }}
        />
      </Field>
      <Field label="Sort order" hint="Lower comes first in pickers.">
        <input
          type="number"
          className="rz-input"
          value={detail.sortOrder ?? ""}
          onChange={(e) => {
            const v = e.target.value.trim();
            patch({ sortOrder: v === "" ? null : Number(v) });
          }}
        />
      </Field>
      <div className="rz-field rz-field--wide">
        <label className="rz-switch">
          <input
            type="checkbox"
            checked={detail.isActive}
            onChange={(e) => patch({ isActive: e.target.checked })}
          />
          Active — available to flows and the runtime
        </label>
      </div>
    </div>
  );
}

/* ---------- Prompt & schema ---------- */

function PromptTab({
  detail,
  patch,
}: {
  detail: RecognizerDetail;
  patch: (p: Partial<RecognizerDetail>) => void;
}) {
  const schema = detail.outputSchema ?? "";
  const schemaState = useMemo(() => {
    if (!schema.trim()) return { ok: true, message: "Empty: the schema is built from the intents and slots." };
    try {
      const parsed = JSON.parse(schema) as unknown;
      const props =
        parsed && typeof parsed === "object" && "properties" in (parsed as Record<string, unknown>)
          ? Object.keys(((parsed as Record<string, unknown>).properties as Record<string, unknown>) ?? {})
          : [];
      return { ok: true, message: props.length ? `Valid JSON, ${props.length} properties.` : "Valid JSON." };
    } catch (ex) {
      return { ok: false, message: `Not valid JSON: ${ex instanceof Error ? ex.message : String(ex)}` };
    }
  }, [schema]);

  const format = () => {
    try {
      patch({ outputSchema: JSON.stringify(JSON.parse(schema), null, 2) });
    } catch {
      /* leave as is; the validity line already explains */
    }
  };

  return (
    <div className="rz-grid">
      <Field
        label="System prompt"
        wide
        hint="What the model is told before it sees the caller's words. Keep it short: every field the model has to produce costs time on each turn."
      >
        <textarea
          className="rz-textarea rz-textarea--code"
          value={detail.systemPrompt ?? ""}
          onChange={(e) => patch({ systemPrompt: e.target.value })}
          rows={18}
          spellCheck={false}
        />
      </Field>
      <Field
        label="Output schema"
        wide
        hint="JSON Schema for the tool call. When set, it wins over the intents and slots below when the runtime builds the request."
      >
        <textarea
          className="rz-textarea rz-textarea--code"
          value={schema}
          onChange={(e) => patch({ outputSchema: e.target.value })}
          rows={10}
          spellCheck={false}
        />
        <div className="rz-inline-actions">
          <span className={schemaState.ok ? "rz-json-ok" : "rz-json-bad"}>{schemaState.message}</span>
          {schema.trim() && schemaState.ok && (
            <button type="button" className="rz-add" onClick={format} style={{ marginTop: 0 }}>
              <Braces size={13} />
              Format JSON
            </button>
          )}
        </div>
      </Field>
    </div>
  );
}

/* ---------- Intents & slots ---------- */

function IntentEditor({
  intents,
  onChange,
}: {
  intents: RecognizerIntentRow[];
  onChange: (rows: RecognizerIntentRow[]) => void;
}) {
  const update = (i: number, p: Partial<RecognizerIntentRow>) =>
    onChange(intents.map((row, idx) => (idx === i ? { ...row, ...p } : row)));
  const add = () =>
    onChange([
      ...intents,
      { recognizerIntentId: null, intentKey: "", displayLabel: "", description: "", sortOrder: intents.length },
    ]);

  return (
    <section className="rz-section">
      <div className="rz-section-head">
        <h3 className="rz-section-title">Intents</h3>
        <span className="rz-section-hint">The categories the model may answer with. Flows branch on the intent key.</span>
      </div>
      <table className="rz-table">
        <thead>
          <tr>
            <th style={{ width: "28%" }}>Key</th>
            <th style={{ width: "30%" }}>Label</th>
            <th>Description</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {intents.length === 0 && (
            <tr>
              <td colSpan={4} className="rz-table-empty">
                No intents yet. Add at least one so the Ask node has something to branch on.
              </td>
            </tr>
          )}
          {intents.map((row, i) => (
            <tr key={i}>
              <td>
                <input
                  type="text"
                  className="rz-input rz-input--code"
                  value={row.intentKey}
                  placeholder="intent_key"
                  onChange={(e) => update(i, { intentKey: e.target.value })}
                />
              </td>
              <td>
                <input
                  type="text"
                  className="rz-input"
                  value={row.displayLabel ?? ""}
                  placeholder="Label"
                  onChange={(e) => update(i, { displayLabel: e.target.value })}
                />
              </td>
              <td>
                <input
                  type="text"
                  className="rz-input"
                  value={row.description ?? ""}
                  placeholder="When the model should pick this"
                  onChange={(e) => update(i, { description: e.target.value })}
                />
              </td>
              <td className="rz-td-remove">
                <button
                  type="button"
                  className="rz-remove"
                  onClick={() => onChange(intents.filter((_, idx) => idx !== i))}
                  title="Remove intent"
                  aria-label="Remove intent"
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="rz-add" onClick={add}>
        <Plus size={13} />
        Add intent
      </button>
    </section>
  );
}

const SLOT_TYPES = ["string", "number", "boolean", "date", "array", "object"];

function SlotEditor({
  slots,
  onChange,
}: {
  slots: RecognizerSlotRow[];
  onChange: (rows: RecognizerSlotRow[]) => void;
}) {
  const update = (i: number, p: Partial<RecognizerSlotRow>) =>
    onChange(slots.map((row, idx) => (idx === i ? { ...row, ...p } : row)));
  const add = () =>
    onChange([
      ...slots,
      { recognizerSlotId: null, slotKey: "", displayLabel: "", dataType: "string", isRequired: false, sortOrder: slots.length },
    ]);

  return (
    <section className="rz-section">
      <div className="rz-section-head">
        <h3 className="rz-section-title">Slots</h3>
        <span className="rz-section-hint">The values the model extracts. These are what a flow can map to variables.</span>
      </div>
      <table className="rz-table">
        <thead>
          <tr>
            <th style={{ width: "28%" }}>Key</th>
            <th>Label</th>
            <th style={{ width: 130 }}>Type</th>
            <th className="rz-td-check">Required</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {slots.length === 0 && (
            <tr>
              <td colSpan={5} className="rz-table-empty">
                No slots yet. Add the values you want to extract, such as postcode or plaats.
              </td>
            </tr>
          )}
          {slots.map((row, i) => {
            const type = row.dataType ?? "string";
            const knownType = SLOT_TYPES.includes(type);
            return (
              <tr key={i}>
                <td>
                  <input
                    type="text"
                    className="rz-input rz-input--code"
                    value={row.slotKey}
                    placeholder="slot_key"
                    onChange={(e) => update(i, { slotKey: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="rz-input"
                    value={row.displayLabel ?? ""}
                    placeholder="Label"
                    onChange={(e) => update(i, { displayLabel: e.target.value })}
                  />
                </td>
                <td>
                  <select
                    className="rz-select"
                    value={type}
                    onChange={(e) => update(i, { dataType: e.target.value })}
                  >
                    {!knownType && <option value={type}>{type}</option>}
                    {SLOT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="rz-td-check">
                  <input
                    type="checkbox"
                    checked={row.isRequired}
                    onChange={(e) => update(i, { isRequired: e.target.checked })}
                    aria-label="Required"
                  />
                </td>
                <td className="rz-td-remove">
                  <button
                    type="button"
                    className="rz-remove"
                    onClick={() => onChange(slots.filter((_, idx) => idx !== i))}
                    title="Remove slot"
                    aria-label="Remove slot"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button type="button" className="rz-add" onClick={add}>
        <Plus size={13} />
        Add slot
      </button>
    </section>
  );
}

/* ---------- Test ---------- */

/**
 * Runs the in-memory form state (not the persisted row) against the LLM,
 * so an admin can iterate on a prompt without saving every change.
 */
function RecognizerTester({ detail }: { detail: RecognizerDetail }) {
  const [inputText, setInputText] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RecognizerTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  const run = async () => {
    if (!inputText.trim()) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await testRecognizer({
        recognizerKey: detail.recognizerKey,
        displayLabel: detail.displayLabel ?? null,
        description: detail.description ?? null,
        llmModel: detail.llmModel?.trim() || null,
        systemPrompt: detail.systemPrompt ?? null,
        outputSchema: detail.outputSchema ?? null,
        intentKeys: detail.intents.map((i) => i.intentKey.trim()).filter((k) => k.length > 0),
        slots: detail.slots
          .map((s) => ({ slotKey: s.slotKey.trim(), dataType: s.dataType ?? null, isRequired: s.isRequired }))
          .filter((s) => s.slotKey.length > 0),
        inputText,
      });
      setResult(res);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="rz-grid">
      <Field
        label="What the caller said"
        wide
        hint="Runs the prompt and schema as they are in this form, saved or not. Ctrl/Cmd+Enter runs it."
      >
        <textarea
          className="rz-textarea"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          rows={3}
          placeholder='e.g. "ik sta op de A28 bij hectometerpaal 46" or "Uithof 12 in Almere"'
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              void run();
            }
          }}
        />
        <div className="rz-inline-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void run()}
            disabled={running || !inputText.trim()}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.9rem", padding: "6px 16px" }}
          >
            <Play size={14} />
            {running ? "Running…" : "Run"}
          </button>
          {result && (
            <span className="rz-hint">
              {result.modelUsed}, {result.latencyMs} ms
              {result.usage?.totalTokens != null ? `, ${result.usage.totalTokens} tokens` : ""}
            </span>
          )}
        </div>
      </Field>

      {error && (
        <div className="rz-field rz-field--wide">
          <div className="rz-notice rz-notice--error" style={{ marginTop: 0 }}>
            {error}
          </div>
        </div>
      )}

      {result && (
        <div className="rz-field rz-field--wide">
          <TestResultView
            result={result}
            showPrompt={showPrompt}
            onTogglePrompt={() => setShowPrompt((s) => !s)}
          />
        </div>
      )}
    </div>
  );
}

function TestResultView({
  result,
  showPrompt,
  onTogglePrompt,
}: {
  result: RecognizerTestResult;
  showPrompt: boolean;
  onTogglePrompt: () => void;
}) {
  const intentColor = result.intentMatched ? "var(--portal-status-success)" : "var(--portal-danger)";
  const slotEntries = Object.entries(result.slots ?? {});
  return (
    <div className="rz-test-result" style={{ marginTop: 0 }}>
      <div className="rz-test-top">
        <div className="rz-test-kv">
          <span className="rz-test-k">Intent</span>
          <span className="rz-test-v">
            <span className="rz-test-dot" style={{ background: intentColor }} />
            <code>{result.intent ?? "(none)"}</code>
            {!result.intentMatched && result.intent && (
              <span className="rz-json-bad">not in the declared intents</span>
            )}
          </span>
        </div>
        {result.confidence != null && (
          <div className="rz-test-kv">
            <span className="rz-test-k">Confidence</span>
            <span className="rz-test-v">{(result.confidence * 100).toFixed(0)}%</span>
          </div>
        )}
      </div>

      <div className="rz-test-slots">
        {slotEntries.length === 0 ? (
          <div className="rz-hint">No slots extracted.</div>
        ) : (
          <table>
            <tbody>
              {slotEntries.map(([k, v]) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td>{formatSlotValue(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rz-test-raw">
        <details>
          <summary>Raw JSON from the model</summary>
          <pre>{prettyJson(result.rawJson)}</pre>
        </details>
        <div style={{ marginTop: 8 }}>
          <button type="button" className="rz-linkbtn" onClick={onTogglePrompt}>
            {showPrompt ? "Hide" : "Show"} the prompt that was sent
          </button>
          {showPrompt &&
            result.promptMessages.map((m, i) => (
              <div key={i} style={{ marginTop: 8 }}>
                <div className="rz-test-k" style={{ marginBottom: 4 }}>{m.role}</div>
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{m.content}</pre>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function prettyJson(raw: string): string {
  if (!raw) return "";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function formatSlotValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/* ---------- shared field ---------- */

function Field({
  label,
  required,
  hint,
  wide,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`rz-field${wide ? " rz-field--wide" : ""}`}>
      <label className="rz-label">
        {label}
        {required && <span className="rz-label-req"> *</span>}
      </label>
      {children}
      {hint && <div className="rz-hint">{hint}</div>}
    </div>
  );
}
